import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuestionnaireStore } from "../store/useQuestionnaireStore";
import { Loader2, X, CheckCircle, XCircle, Calendar, User, Mail } from "lucide-react";
import QuestionInput from "../components/questionnaires/QuestionInput";

const QuestionnaireDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { getQuestionnaire } = useQuestionnaireStore();
  const [questionnaire, setQuestionnaire] = useState<any>(null);

  useEffect(() => {
    if (id) {
      getQuestionnaire(id).then((data: any) => setQuestionnaire(data));
    }
  }, [id]);

  if (!questionnaire) {
    return (
      <div className="flex flex-col items-center justify-center py-16 space-y-4">
        <Loader2 className="animate-spin text-[#2462b2]" size={40} />
        <p className="text-gray-500 font-medium">Loading questionnaires...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f8f9ff] pt-20 px-4 lg:px-8 pb-10">
      <div className="max-w-6xl mx-auto">
        <div className="bg-white rounded-xl border border-slate-200 shadow-[0_4px_6px_-1px_rgba(0,0,0,0.05)] overflow-hidden">
          <div className="bg-[#0058c3] p-8 text-white">
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <h1 className="text-3xl font-extrabold mb-2">{questionnaire.name}</h1>
                <p className="text-blue-100 text-sm">{questionnaire.description}</p>
              </div>
              <button
                onClick={() => navigate(-1)}
                className="p-3 rounded-full hover:bg-white/20 transition-all duration-200"
              >
                <X size={24} />
              </button>
            </div>
          </div>

          <div className="p-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              <div className="bg-gradient-to-br from-blue-50 to-blue-100/50 rounded-xl p-5 border border-blue-200">
                <h3 className="text-sm font-bold text-gray-600 mb-3 uppercase tracking-wider">Project Details</h3>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium text-gray-500">Service Type:</span>
                    <span className="text-sm font-bold text-gray-800">{questionnaire.serviceType}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium text-gray-500">Project:</span>
                    <span className="text-sm font-bold text-gray-800">{questionnaire.projectId?.name || "—"}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium text-gray-500">Contact:</span>
                    <span className="text-sm font-bold text-gray-800">{questionnaire.clientId?.name || "—"}</span>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-purple-50 to-purple-100/50 rounded-xl p-5 border border-purple-200">
                <h3 className="text-sm font-bold text-gray-600 mb-3 uppercase tracking-wider">Submission Status</h3>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    {questionnaire.isSubmitted ? (
                      <>
                        <CheckCircle size={20} className="text-emerald-600" />
                        <span className="text-sm font-bold text-emerald-700">Submitted</span>
                      </>
                    ) : (
                      <>
                        <XCircle size={20} className="text-yellow-600" />
                        <span className="text-sm font-bold text-yellow-700">Pending</span>
                      </>
                    )}
                  </div>
                  {questionnaire.submittedAt && (
                    <div className="flex items-center gap-3">
                      <Calendar size={18} className="text-gray-500" />
                      <span className="text-sm text-gray-700">
                        {new Date(questionnaire.submittedAt).toLocaleString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {questionnaire.isSubmitted && (questionnaire.respondentName || questionnaire.respondentEmail) && (
              <div className="bg-gradient-to-br from-green-50 to-green-100/50 rounded-xl p-6 border border-green-200 mb-8">
                <h3 className="text-sm font-bold text-gray-700 mb-4 uppercase tracking-wider flex items-center gap-2">
                  <User size={18} className="text-green-600" />
                  Respondent Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {questionnaire.respondentName && (
                    <div className="flex items-center gap-3">
                      <User size={16} className="text-gray-500" />
                      <div>
                        <p className="text-xs text-gray-500 font-medium">Name</p>
                        <p className="text-sm font-bold text-gray-800">{questionnaire.respondentName}</p>
                      </div>
                    </div>
                  )}
                  {questionnaire.respondentEmail && (
                    <div className="flex items-center gap-3">
                      <Mail size={16} className="text-gray-500" />
                      <div>
                        <p className="text-xs text-gray-500 font-medium">Email</p>
                        <p className="text-sm font-bold text-gray-800">{questionnaire.respondentEmail}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="flex items-center gap-4 mb-6">
              <div className="flex-1 h-px bg-gradient-to-r from-transparent via-gray-300 to-transparent"></div>
              <h2 className="text-xl font-bold text-gray-800 uppercase tracking-wider">
                {questionnaire.isSubmitted ? "Contact Responses" : "Questions & Answers"}
              </h2>
              <div className="flex-1 h-px bg-gradient-to-r from-transparent via-gray-300 to-transparent"></div>
            </div>

            <div className="space-y-6">
              {questionnaire.questions.map((q: any, idx: number) => (
                <div key={q._id} className="bg-gradient-to-br from-gray-50 to-gray-100/50 border border-gray-200 rounded-xl p-6 hover:shadow-lg transition-all duration-200">
                  <div className="flex items-start justify-between mb-4">
                    <label className="font-bold text-gray-900 text-lg flex-1">
                      <span className="inline-block w-8 h-8 bg-gradient-to-r from-[#2462b2] to-[#1e55a0] text-white rounded-full text-center leading-8 mr-3 text-sm">
                        {idx + 1}
                      </span>
                      {q.question}
                      {q.required && <span className="text-red-500 ml-2">*</span>}
                    </label>
                    <span className="text-xs font-bold px-3 py-1 bg-blue-100 text-blue-700 rounded-full uppercase tracking-wider">
                      {q.answerType}
                    </span>
                  </div>

                  {questionnaire.isSubmitted ? (
                    <div className="bg-white rounded-lg p-4 border-2 border-blue-100">
                      <p className="text-xs text-gray-500 font-medium mb-2 uppercase tracking-wider">Contact Answer:</p>
                      <QuestionInput
                        question={q}
                        value={q.answer ?? (q.answerType === "checkbox" ? [] : "")}
                        onChange={() => {}}
                        disabled={true}
                      />
                    </div>
                  ) : (
                    <div className="bg-white rounded-lg p-4 border-2 border-gray-200">
                      <p className="text-xs text-gray-500 font-medium mb-2 uppercase tracking-wider">Expected Answer Format:</p>
                      <QuestionInput
                        question={q}
                        value={q.answer ?? (q.answerType === "checkbox" ? [] : "")}
                        onChange={() => {}}
                        disabled={true}
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>

            {!questionnaire.isSubmitted && (
              <div className="mt-8 p-6 bg-yellow-50 border-2 border-yellow-200 rounded-xl">
                <p className="text-sm text-yellow-800 font-medium text-center">
                  This questionnaire has not been submitted yet. Answers will appear here once the client completes the form.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuestionnaireDetailsPage;
